/**
 * Enhanced Notification Bell - Works across ALL dashboard types
 * Auto-refreshes every 15 seconds, shows unread badge
 * Auto-subscribes to push notifications when permission granted
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, CheckCheck, Clock, Inbox, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDistanceToNow } from 'date-fns';
import { fetchNotifications, markNotificationRead, markAllNotificationsRead, showBrowserNotification, type AppNotification } from '@/utils/notificationService';
import { subscribeToPush, getPushStatus } from '@/utils/pushSubscription';
import { updateAppBadge } from '@/utils/appBadge';
import { playNotificationSound } from '@/utils/notificationSound';
import { useToast } from '@/hooks/use-toast';

interface NotificationBellProps {
  dashboardType: string; // member, patient, blood_bank, ambulance, homecare, healthcamp
  entityId: number | string;
  /** "View all notifications" footer button — should point at the real notifications history page. */
  onViewAll?: () => void;
  /** Individual item click — typically navigate to notif.action_url, falling back to onViewAll. */
  onNotificationClick?: (notif: AppNotification) => void;
  autoRefreshInterval?: number; // ms, default 15000
}

const NotificationBell = ({
  dashboardType,
  entityId,
  onViewAll,
  onNotificationClick,
  autoRefreshInterval = 15000
}: NotificationBellProps) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevIdsRef = useRef<Set<string | number>>(new Set());
  // Separate from prevIdsRef.current.size — that conflates "this is the very
  // first poll" with "the previous poll simply had zero unread items" (the
  // common case: dashboard opens with everything already read). Both looked
  // identical to the old `prevIdsRef.current.size > 0` check, so the sound/
  // browser-notification never fired for the first genuinely new item to
  // arrive after a clean inbox — confirmed live: a fresh notification always
  // landed silently whenever unread_count started at 0.
  const hasLoadedOnceRef = useRef(false);
  const { toast } = useToast();
  // Still-undecided ('default') push permission — the bell rings itself to draw the eye,
  // since a visitor who never got (or dismissed) the auto-prompt at login has no other
  // obvious way to discover that tapping it will ask again.
  const [needsPushPermission, setNeedsPushPermission] = useState(false);

  // Auto-subscribe to push notifications
  useEffect(() => {
    if (!entityId) return;
    const { supported, permission } = getPushStatus();
    setNeedsPushPermission(supported && permission === 'default');
    if (supported && permission !== 'denied') {
      subscribeToPush(entityId, dashboardType)
        .then(() => setNeedsPushPermission(getPushStatus().permission === 'default'))
        .catch(() => {});
    }
  }, [entityId, dashboardType]);

  const handleBellClick = () => {
    if (needsPushPermission && entityId) {
      subscribeToPush(entityId, dashboardType)
        .then(() => setNeedsPushPermission(getPushStatus().permission === 'default'))
        .catch(() => {});
    }
  };

  const loadNotifications = useCallback(async () => {
    if (!entityId) return;
    try {
      // unreadOnly: once something is marked read (click or "Read all"), it must never
      // reappear in this dropdown on the next 15s poll — the dropdown's job is "what's
      // new", not a full history (that's the dedicated Notifications page).
      const result = await fetchNotifications(dashboardType, entityId, { limit: 10, unreadOnly: true });

      // Detect NEW notifications for browser notification + sound
      const newIds = new Set(result.notifications.map(n => n.id));
      const brandNew = result.notifications.find(n => !prevIdsRef.current.has(n.id));
      if (brandNew && hasLoadedOnceRef.current) {
        showBrowserNotification(brandNew.title, brandNew.message, brandNew.action_url);
        playNotificationSound();
      }
      prevIdsRef.current = newIds;
      hasLoadedOnceRef.current = true;

      setNotifications(result.notifications);
      setUnreadCount(result.unread_count);
      updateAppBadge(result.unread_count);
    } catch {
      // Silent fail
    }
  }, [dashboardType, entityId]);

  // Initial load
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const interval = setInterval(loadNotifications, autoRefreshInterval);
    return () => clearInterval(interval);
  }, [loadNotifications, autoRefreshInterval]);

  const handleMarkRead = async (id: string | number) => {
    try {
      await markNotificationRead(id, dashboardType, entityId);
      // Remove from the dropdown immediately — it's unread-only, so a read item has no
      // reason to still be listed here (this is what was previously reported as
      // "click karne par bhi wahi dikhta hai, remove nahi hota").
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const next = Math.max(0, prev - 1);
        updateAppBadge(next);
        return next;
      });
    } catch (error) {
      toast({
        title: 'Could not mark as read',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(dashboardType, entityId);
      setNotifications([]);
      setUnreadCount(0);
      updateAppBadge(0);
    } catch (error) {
      toast({
        title: 'Could not mark all as read',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleItemClick = (notif: AppNotification) => {
    handleMarkRead(notif.id);
    setOpen(false);
    if (onNotificationClick) onNotificationClick(notif);
    else onViewAll?.();
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'booking': case 'inquiry': return 'bg-yellow-500';
      case 'confirmation': case 'success': return 'bg-green-500';
      case 'warning': return 'bg-orange-500';
      case 'error': return 'bg-destructive';
      default: return 'bg-primary';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={handleBellClick}>
          <Button variant="ghost" size="icon" className="relative hover:bg-primary/10 rounded-xl">
            <motion.div
              animate={needsPushPermission ? { rotate: [0, -18, 15, -12, 9, -5, 0] } : {}}
              transition={needsPushPermission ? { duration: 0.9, repeat: Infinity, repeatDelay: 2, ease: 'easeInOut' } : {}}
            >
              <Bell className={needsPushPermission ? 'w-5 h-5 text-primary' : 'w-5 h-5'} />
            </motion.div>
            {needsPushPermission && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
                className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-primary rounded-full"
              />
            )}
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-[#ff3b3b] text-white text-xs rounded-full flex items-center justify-center shadow-lg font-medium"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </motion.span>
            )}
          </Button>
        </motion.div>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-card/95 backdrop-blur-xl border-border rounded-xl shadow-2xl"
        align="end"
      >
        {/* Header */}
        <div className="p-4 border-b border-border/50 bg-gradient-to-r from-primary/5 to-primary/10">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleMarkAllRead}>
                  <CheckCheck className="w-3 h-3 mr-1" /> Read all
                </Button>
              )}
              {unreadCount > 0 && (
                <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
                  {unreadCount} new
                </Badge>
              )}
            </div>
          </div>
          {unreadCount > notifications.length && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Showing {notifications.length} of {unreadCount} unread — view all for the rest
            </p>
          )}
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Inbox className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                New updates will appear here
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              <AnimatePresence initial={false}>
              {notifications.map((notif, idx) => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className={`p-3 hover:bg-muted/50 transition-colors cursor-pointer ${
                    !notif.is_read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleItemClick(notif)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${notif.is_read ? 'bg-muted-foreground/30' : getTypeColor(notif.type)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{notif.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </div>
                    {!notif.is_read && (
                      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] bg-primary/10 text-primary">
                        New
                      </Badge>
                    )}
                  </div>
                </motion.div>
              ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && onViewAll && (
          <div className="p-3 border-t border-border/50">
            <Button
              variant="ghost"
              className="w-full justify-between text-sm h-9 hover:bg-primary/10"
              onClick={() => { setOpen(false); onViewAll(); }}
            >
              View all notifications
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Auto-refresh indicator */}
        <div className="px-3 pb-2">
          <p className="text-[10px] text-muted-foreground/50 text-center">
            Auto-refreshes every {Math.round(autoRefreshInterval / 1000)}s
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
